import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import { useLocation, useNavigate } from "react-router-dom";
import Calendar from '../components/Calendar';
import CourtView from '../components/CourtView';
import BookingModal from '../components/BookingModal';
import BookingSummaryModal from '../components/BookingSummaryModal';
import DeletionSummaryModal from '../components/DeletionSummaryModal';
import BookingDetailsModal from '../components/BookingDetailsModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ErrorModal from '../components/ErrorModal';
import { getBookingsByUser, getBookingsInRange, deleteBooking, createBooking, getCourts, getBookingCategories, getBookingOptions, getClubDetails, ErrorType, updateBookingStatus, updateBooking } from '../api';
import BookingGroup from '../components/BookingGroup';
import PaymentModal from '../components/PaymentModal';
import Tooltip from '../components/Tooltip';
import CustomDatePicker from '../components/CustomDatePicker';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameById } from '../utils/courtDisplay';

// Helper functions moved outside of components
const getUniqueCourts = (bookings) => {
  return [...new Set(bookings.map(b => b.court))].sort((a, b) => parseInt(a) - parseInt(b));
};

/** Minutes from midnight for "HH:MM" — hour-only sorts wrongly tie 12:00 with 12:30. */
const startTimeToMinutes = (startTime) => {
  if (!startTime || typeof startTime !== 'string') return 0;
  const parts = startTime.split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const compareBookingsByStart = (a, b) => {
  const dateA = new Date(a.bookingDate);
  const dateB = new Date(b.bookingDate);
  if (dateA.getTime() !== dateB.getTime()) return dateA - dateB;
  return startTimeToMinutes(a.startTime) - startTimeToMinutes(b.startTime);
};

// Helper function to calculate all booking PKs/SKs for deletion
const calculateBookingKeys = (booking, recurringGroups) => {
  const keys = [];
  const excludedBookings = []; // Track bookings that couldn't be deleted due to joiners
  
  console.log('calculateBookingKeys called with:', { booking, recurringGroups });
  
  if (booking.bookingId) {
    // Get all bookings in the group (recurring or multi-court)
    // Since getBookingsByUser already returns only future bookings (SK >= today),
    // we can use all grouped bookings directly without additional filtering
    const groupedBookings = recurringGroups[booking.bookingId] || [];
    
    if (groupedBookings.length > 0) {
      console.log(`Using existing grouped bookings: ${groupedBookings.length} future bookings`);
      
      groupedBookings.forEach(b => {
        // Check if this booking has joiners (public booking with confirmed or pending users)
        const hasJoiners = checkBookingHasJoiners(b);
        
        if (hasJoiners) {
          console.log(`Excluding booking ${b.court} on ${b.bookingDate} - has joiners`);
          excludedBookings.push(b);
        } else {
          keys.push({
            bookingDate: b.bookingDate,
            courtStartTime: `${b.court}#${b.startTime}`
          });
        }
      });
    } else {
      // This should rarely happen since getBookingsByUser should return all future bookings
      console.warn(`No grouped bookings found for bookingId ${booking.bookingId}, falling back to single booking`);
      const hasJoiners = checkBookingHasJoiners(booking);
      
      if (hasJoiners) {
        console.log(`Excluding single booking ${booking.court} on ${booking.bookingDate} - has joiners`);
        excludedBookings.push(booking);
      } else {
        keys.push({
          bookingDate: booking.bookingDate,
          courtStartTime: `${booking.court}#${booking.startTime}`
        });
      }
    }
  } else {
    // Single booking
    const hasJoiners = checkBookingHasJoiners(booking);
    
    if (hasJoiners) {
      console.log(`Excluding single booking ${booking.court} on ${booking.bookingDate} - has joiners`);
      excludedBookings.push(booking);
    } else {
      keys.push({
        bookingDate: booking.bookingDate,
        courtStartTime: `${booking.court}#${booking.startTime}`
      });
    }
  }
  
  console.log(`calculateBookingKeys returning ${keys.length} keys and ${excludedBookings.length} excluded bookings`);
  return { keys, excludedBookings };
};

// Helper function to check if a booking has joiners
const checkBookingHasJoiners = (booking) => {
  if (!booking.public) {
    return false; // Not a public booking, no joiners
  }
  
  try {
    const publicData = typeof booking.public === 'string' ? JSON.parse(booking.public) : booking.public;
    const confirmedCount = Object.keys(publicData.confirmed || {}).length;
    const pendingCount = Object.keys(publicData.pending || {}).length;
    
    return confirmedCount > 0 || pendingCount > 0;
  } catch (error) {
    console.error('Error parsing public data:', error);
    return false; // If we can't parse, assume no joiners
  }
};

const getUniqueSports = (courts) => {
  const sports = [...new Set(courts.map(court => court.court.split('-')[0]))];
  if (sports.length === 0) return [];
  const sportIndexes = courts.reduce((acc, court) => {
    const sport = court.court.split('-')[0];
    const index = Number(court.sportIndex);
    if (!Number.isFinite(index)) return acc;
    if (acc[sport] === undefined || index < acc[sport]) {
      acc[sport] = index;
    }
    return acc;
  }, {});
  const ordered = [...sports].sort((a, b) => {
    const aIndex = sportIndexes[a];
    const bIndex = sportIndexes[b];
    if (aIndex === undefined && bIndex === undefined) {
      return a.localeCompare(b);
    }
    if (aIndex === undefined) return 1;
    if (bIndex === undefined) return -1;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return a.localeCompare(b);
  });
  return ordered.length > 1 ? ['All Courts', ...ordered] : ordered;
};

const Dashboard = () => {
  const { user, loginUser } = useContext(AuthContext);
  const { clubDetails, setClubDetails, bookingCategories, setBookingCategories, bookingOptions, setBookingOptions, setCourtData } = useClub();
  const navigate = useNavigate();
  const location = useLocation();
  const deepLinkHandledRef = useRef('');

  console.log('Dashboard component rendered, user exists:', !!user);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);
  const [courts, setCourts] = useState([]);
  const [courtsLoaded, setCourtsLoaded] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    // Get the current time in UTC
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000)); // Adjust to UTC
    // Adjust to South Africa time (UTC+2)
    const southAfricaTime = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
    return southAfricaTime;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    booking: null
  });
  const [bookingSummary, setBookingSummary] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [deletionSummary, setDeletionSummary] = useState(null);
  const [showDeletionSummaryModal, setShowDeletionSummaryModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editMode, setEditMode] = useState('full');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const getCourtDisplayLabel = (courtId) => getCourtDisplayNameById(courtId, courts, 'Court');
  const [localTimeSlots, setLocalTimeSlots] = useState({});
  const [initialSlotsLoaded, setInitialSlotsLoaded] = useState(false);
  const [selectedSport, setSelectedSport] = useState('All Courts');
  const availableSports = React.useMemo(
    () => getUniqueSports(courts),
    [courts]
  );
  const hasAnyCourtDisplayName = React.useMemo(
    () => courts.some((court) => String(court?.displayName || '').trim().length > 0),
    [courts]
  );

  const getCategoryIdForCourtAndName = React.useCallback((courtId, categoryName) => {
    if (!courtId || !categoryName) return '';
    const courtType = String(courtId).split('-')[0];
    return bookingCategories.find(
      (category) => category.type === courtType && category.name === categoryName
    )?.id || '';
  }, [bookingCategories]);

  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('courtBookingPreferences');
    return saved ? JSON.parse(saved).viewMode || 'Calendar' : 'Calendar';
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [courtViewBookings, setCourtViewBookings] = useState([]);

  // Helper function to generate customText for booking payments
  const generateBookingPaymentText = (successfulBookings) => {
    // Check if any court has floodlights but no smart switches
    const hasFloodlightsWithoutSwitches = successfulBookings.some(booking => {
      const courtData = courts.find(c => c.court === booking.court);
      return courtData?.floodlights === true && (!courtData.switches || courtData.switches.length === 0);
    });

    const floodlightsBullet = hasFloodlightsWithoutSwitches 
      ? '<li>Tokens for floodlights are NOT included</li>'
      : '';

    const contactText = hasFloodlightsWithoutSwitches
      ? 'Contact the club to arrange access and tokens'
      : 'Contact the club to arrange access';

    return `
      <ul class="list-disc pl-5">
        <li>Payments are NON-REFUNDABLE</li>
        ${floodlightsBullet}
        <li>${contactText}</li>
      </ul>
    `;
  };

  // Helper function to save preferences to localStorage
  const savePreferences = (newPreferences) => {
    const current = JSON.parse(localStorage.getItem('courtBookingPreferences') || '{}');
    const updated = { ...current, ...newPreferences };
    localStorage.setItem('courtBookingPreferences', JSON.stringify(updated));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCourts = async () => {
    try {
      console.log('fetchCourts called');
      const courtsData = await getCourts();
      console.log('************* courtsData', courtsData);
      
      setCourts(courtsData);
      setCourtsLoaded(true);
      
      // Set the first available court as selected by default
      if (courtsData.length > 0) {
        const firstCourt = courtsData[0];
        console.log('************* firstCourt', firstCourt);
        setSelectedCourt(firstCourt.court);
        
        // Generate time slots for all courts
        const allSlots = {};
        courtsData.forEach(court => {
          const openingTime = court.openingTime; // e.g., '06:00'
          const closingTime = court.closingTime; // e.g., '22:00'
          const slotDuration = parseInt(court.slotDuration, 10); // e.g., 30 or 60

          const slots = [];
          let currentTime = new Date(`1970-01-01T${openingTime}:00`); // Use a fixed date

          // Generate time slots
          while (currentTime.getHours() < parseInt(closingTime.split(':')[0], 10) || 0) {
            slots.push(currentTime.toTimeString().slice(0, 5)); // Format as HH:MM
            currentTime.setMinutes(currentTime.getMinutes() + slotDuration); // Increment by slot duration
          }

          allSlots[court.court] = slots; // Store slots by court ID
        });

        setLocalTimeSlots(allSlots); // Store all generated slots
        setCourtData(courtsData); // Store court data in context
        setInitialSlotsLoaded(true); // Mark initial slots as loaded
      }
    } catch (err) {
      switch (err.type) {
        case ErrorType.NETWORK:
          handleError('Unable to load courts. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to load courts. Please try again later.');
          break;
        default:
          handleError('Failed to load courts. Please try again.');
      }
      setCourtsLoaded(false);
    }
  };

  const fetchBookingCategories = async () => {
    try {
      const categoriesData = await getBookingCategories();
      console.log('Booking Categories:', categoriesData);
      setBookingCategories(categoriesData);
      setCategoriesLoaded(true);
    } catch (err) {
      switch (err.type) {
        case ErrorType.NETWORK:
          handleError('Unable to load booking categories. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to load booking categories. Please try again later.');
          break;
        default:
          handleError('Failed to load booking categories. Please try again.');
      }
      setCategoriesLoaded(false);
    }
  };

  const fetchBookingOptions = async () => {
    try {
      const optionsData = await getBookingOptions();
      console.log('Booking Options:', optionsData);
      setBookingOptions(optionsData);
    } catch (err) {
      switch (err.type) {
        case ErrorType.NETWORK:
          handleError('Unable to load booking options. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to load booking options. Please try again later.');
          break;
        default:
          handleError('Failed to load booking options. Please try again.');
      }
    }
  };

  const fetchClubDetails = async () => {
    try {
      const clubDetailsData = await getClubDetails();
      console.log('************* clubDetailsData', clubDetailsData);
      setClubDetails(clubDetailsData);
    } catch (err) {
      switch (err.type) {
        case ErrorType.NETWORK:
          handleError('Unable to load club details. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to load club details. Please try again later.');
          break;
        default:
          handleError('Failed to load club details. Please try again.');
      }
    }
  };

  useEffect(() => {
    console.log('Dashboard useEffect triggered - starting API calls');
    // Start all fetches immediately in parallel - no dependencies between them
    fetchCourts();
    fetchBookingCategories();
    fetchBookingOptions();
    fetchClubDetails();
    // Bookings can be fetched immediately - they don't depend on courts/categories
    // currentDate and viewMode are initialized via useState, so they're always defined on mount
    fetchUserBookings();

    // If this page was opened via a deep link, load bookings for that linked date immediately
    // to avoid a race where the default date fetch overwrites Court View data.
    const params = new URLSearchParams(window.location.search);
    const deepLinkBookingId = params.get('b');
    const deepLinkBookingDate = params.get('d');
    const parsedDeepLinkDate = deepLinkBookingDate ? new Date(`${deepLinkBookingDate}T00:00:00`) : null;

    if (deepLinkBookingId && deepLinkBookingDate && parsedDeepLinkDate && !Number.isNaN(parsedDeepLinkDate.getTime())) {
      const now = new Date();
      const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const clubNow = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
      const todayDateStr = formatDateToString(clubNow);

      if (deepLinkBookingDate >= todayDateStr) {
        if (viewMode === 'Date') {
          setSelectedDate(parsedDeepLinkDate);
        } else {
          setCurrentDate(parsedDeepLinkDate);
        }
        fetchBookingsForView(parsedDeepLinkDate, viewMode);
        return;
      }
    }

    fetchBookingsForView(currentDate, viewMode);
  }, []);

  useEffect(() => {
    if (courts.length > 0 && categoriesLoaded) {
      // Load and validate saved selectedSport against available sports
      const savedPreferences = JSON.parse(localStorage.getItem('courtBookingPreferences') || '{}');
      
      // Check if current selectedCourt is still valid
      const currentCourtIsValid = selectedCourt && courts.some(c => c.court === selectedCourt);
      const selectedCourtMatchesSport = (sport) => {
        if (!sport || sport === 'All Courts') return true;
        if (!selectedCourt) return false;
        return selectedCourt.startsWith(sport);
      };
      
      // Only load/validate selectedSport if we have multiple sports (single sport clubs don't use selectedSport)
      if (availableSports.length > 1) {
        if (savedPreferences.selectedSport && availableSports.includes(savedPreferences.selectedSport)) {
          // Valid saved sport, use it
          setSelectedSport(savedPreferences.selectedSport);
          // Ensure selectedCourt is valid and matches the saved sport
          if (!currentCourtIsValid || !selectedCourtMatchesSport(savedPreferences.selectedSport)) {
            if (savedPreferences.selectedSport !== 'All Courts') {
              const sportCourts = courts.filter(court => court.court.startsWith(savedPreferences.selectedSport));
              if (sportCourts.length > 0) {
                setSelectedCourt(sportCourts[0].court);
              }
            } else {
              // For 'All Courts', set to first available court
              setSelectedCourt(courts.length > 0 ? courts[0].court : null);
            }
          }
        } else {
          // No saved sport or invalid sport, use 'All Courts' and update localStorage
          setSelectedSport('All Courts');
          savePreferences({ selectedSport: 'All Courts' });
          // Only update selectedCourt if it's not already set and valid
          if (!currentCourtIsValid) {
            setSelectedCourt(courts.length > 0 ? courts[0].court : null);
          }
        }
      } else {
        // Single sport club, ensure we're using the only available sport
        setSelectedSport(availableSports[0]);
        // Only update selectedCourt if it's not already set and valid
        if (!currentCourtIsValid) {
          setSelectedCourt(courts.length > 0 ? courts[0].court : null);
        }
      }
    }
  }, [user, courts, categoriesLoaded, availableSports, selectedCourt]);

  const getStartOfWeek = (date) => {
    date.setHours(0, 0, 0, 0); // Set to midnight
    return date;
  };

  const getEndOfWeek = (startDate, isSmallScreen) => {
    const end = new Date(startDate);
    end.setDate(end.getDate() + (isSmallScreen ? 2 : 6)); // Show 3 days or 7 days
    end.setHours(23, 59, 59, 999); // Set to end of the day
    return end;
  };

  // Filter out bookings where end time is in the past
  const filterPastBookings = (bookings) => {
    const now = new Date();
    // Get the current time in UTC
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000)); // Adjust to UTC
    // Adjust to South Africa time (UTC+2)
    const currentTime = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));

    return bookings.filter(booking => {
      const bookingDate = new Date(booking.bookingDate);
      const [endHour, endMinute] = booking.endTime.split(':').map(Number);
      const endTime = new Date(bookingDate);
      endTime.setHours(endHour, endMinute, 0, 0);
      
      // Only show bookings that haven't ended yet
      return endTime >= currentTime;
    });
  };

  // Helper to extract exceptions for a specific court from a BLOCK recurring rule.
  const getBlockExceptionsForCourt = (recurringRule, court) => {
    if (!recurringRule || !recurringRule.exceptions) return [];

    const { exceptions } = recurringRule;

    // Legacy format: exceptions is an array applying to all courts
    if (Array.isArray(exceptions)) {
      return exceptions;
    }

    // Per-court format: exceptions is an object keyed by court name
    if (typeof exceptions === 'object' && court && Array.isArray(exceptions[court])) {
      return exceptions[court];
    }

    return [];
  };

  // Helper function to calculate next date for a BLOCK booking.
  // For daily/weekly BLOCK bookings, if there is an occurrence today that has not yet ended,
  // we return today's date; otherwise we return the next non-exception occurrence date.
  const calculateNextBlockBookingDate = (recurringRule, booking) => {
    if (!recurringRule || recurringRule.duration !== -1) {
      return null; // Only process BLOCK bookings (duration === -1)
    }

    if (!booking || !booking.startTime || !booking.endTime) {
      console.warn('calculateNextBlockBookingDate called without booking time information');
      return null;
    }

    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const clubNow = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000)); // SA time

    // Get any exception dates for this court (YYYY-MM-DD strings)
    const exceptions = getBlockExceptionsForCourt(recurringRule, booking.court);
    const exceptionSet = new Set(exceptions);

    // Helper to build a Date at club time for a given date string and time string
    const buildDateTime = (dateStr, timeStr) => {
      const [hour, minute] = timeStr.split(':').map(Number);
      const d = new Date(`${dateStr}T00:00:00`);
      d.setHours(hour, minute, 0, 0);
      return d;
    };

    if (recurringRule.frequency === 'daily') {
      // For daily BLOCK bookings, walk forward day by day until we find
      // a day whose occurrence hasn't ended yet and isn't in the exceptions list.
      // But only consider dates on or after the rule's startDate
      const ruleStartDate = recurringRule.startDate ? new Date(recurringRule.startDate + 'T00:00:00') : null;
      const candidate = new Date(clubNow);
      candidate.setHours(0, 0, 0, 0);
      
      // If rule has a startDate and we're before it, start from the rule's startDate
      if (ruleStartDate && candidate < ruleStartDate) {
        candidate.setTime(ruleStartDate.getTime());
        candidate.setHours(0, 0, 0, 0);
      }

      for (let i = 0; i < 366; i++) {
        // Skip dates before the rule's startDate
        if (ruleStartDate && candidate < ruleStartDate) {
          candidate.setDate(candidate.getDate() + 1);
          continue;
        }
        
        const dateStr = formatDateToString(candidate);
        const endTime = buildDateTime(dateStr, booking.endTime);

        if (endTime >= clubNow && !exceptionSet.has(dateStr)) {
          return dateStr;
        }

        // Move to next day
        candidate.setDate(candidate.getDate() + 1);
      }

      return null;
    } else if (recurringRule.frequency === 'weekly' && recurringRule.day) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const targetDayIndex = dayNames.indexOf(recurringRule.day);

      if (targetDayIndex === -1) {
        console.warn(`Invalid day name in recurringRule: ${recurringRule.day}`);
        return null;
      }

      const todayStr = formatDateToString(clubNow);
      const today = new Date(`${todayStr}T00:00:00`);

      // For weekly BLOCK bookings, walk forward week by week looking for
      // the first occurrence (on the configured weekday) that hasn't ended yet
      // and isn't in the court's exceptions list.
      let current = new Date(today);

      for (let i = 0; i < 60; i++) {
        const currentDay = current.getDay();
        let daysAhead = (targetDayIndex - currentDay + 7) % 7;
        if (daysAhead === 0 && i > 0) {
          daysAhead = 7; // always move forward to a future occurrence
        }

        current.setDate(current.getDate() + daysAhead);

        const dateStr = formatDateToString(current);
        const endTime = buildDateTime(dateStr, booking.endTime);

        if (endTime >= clubNow && !exceptionSet.has(dateStr)) {
          return dateStr;
        }

        // Move to the next day to continue searching
        current.setDate(current.getDate() + 1);
      }

      return null;
    }

    return null;
  };

  const fetchUserBookings = async () => {
    try {
      const userBookings = await getBookingsByUser();
      const refreshedToken = userBookings?._token || null;
      if (refreshedToken) {
        loginUser(refreshedToken);
      }
      
      // Process BLOCK bookings - calculate next date for display
      const processedBookings = userBookings.map(booking => {
        if (booking.bookingDate === 'BLOCK' && booking.isRecurring && booking.recurringRule) {
          let recurringRule;
          try {
            recurringRule = typeof booking.recurringRule === 'string' 
              ? JSON.parse(booking.recurringRule) 
              : booking.recurringRule;
          } catch (e) {
            console.error(`Error parsing recurringRule for BLOCK booking ${booking.bookingId}:`, e);
            return booking;
          }

          const nextDate = calculateNextBlockBookingDate(recurringRule, booking);
          if (nextDate) {
            return {
              ...booking,
              bookingDate: nextDate // Replace 'BLOCK' with calculated next date for display
            };
          }
        }
        return booking;
      });
      
      // Filter out past bookings (this will work for BLOCK bookings with calculated dates)
      const filteredBookings = filterPastBookings(processedBookings);
      
      // Sort bookings by date and start time (including minutes)
      const sortedBookings = filteredBookings.sort(compareBookingsByStart);

      setUserBookings(sortedBookings);
    } catch (err) {
      switch (err.type) {
        case ErrorType.NETWORK:
          handleError('Unable to load your bookings. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to load your bookings. Please try again later.');
          break;
        default:
          handleError('Failed to load your bookings. Please try again.');
      }
    }
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateToString = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to calculate dates for a BLOCK booking based on recurringRule
  const calculateBlockBookingDates = (recurringRule, startDate, endDate, court = null) => {
    // Only process BLOCK bookings (duration === -1)
    if (!recurringRule) return [];
    if (recurringRule.duration !== -1) return []; // Only process BLOCK bookings
    
    // At this point, we know duration === -1 (BLOCK booking)

    const start = new Date(startDate + 'T00:00:00'); // Ensure proper date parsing
    const end = new Date(endDate + 'T23:59:59');
    const matchingDates = [];

    // Get exceptions - handle both per-court format (object) and legacy format (array)
    let exceptions = [];
    if (recurringRule.exceptions) {
      if (Array.isArray(recurringRule.exceptions)) {
        // Legacy format: exceptions apply to all courts
        exceptions = recurringRule.exceptions;
      } else if (typeof recurringRule.exceptions === 'object' && court) {
        // Per-court format: get exceptions for this specific court
        exceptions = recurringRule.exceptions[court] || [];
      } else if (typeof recurringRule.exceptions === 'object' && !court) {
        // Per-court format but no court specified - merge all exceptions
        exceptions = Object.values(recurringRule.exceptions).flat();
      }
    }
    const exceptionSet = new Set(exceptions);

    if (recurringRule.frequency === 'daily') {
      // Daily BLOCK booking applies to all dates in range, but only from the rule's startDate
      // Get the start date from the recurring rule (when the block booking started)
      const ruleStartDate = recurringRule.startDate ? new Date(recurringRule.startDate + 'T00:00:00') : start;
      
      // Use the later of the rule start date or the calendar view start date
      const effectiveStart = ruleStartDate > start ? ruleStartDate : start;
      const current = new Date(effectiveStart);
      
      while (current <= end) {
        // Only include dates that are on or after the rule start date
        const currentDateOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());
        const ruleStartDateOnly = new Date(ruleStartDate.getFullYear(), ruleStartDate.getMonth(), ruleStartDate.getDate());
        
        if (currentDateOnly >= ruleStartDateOnly) {
          const dateStr = formatDateToString(current);
          // Exclude dates in exceptions
          if (!exceptionSet.has(dateStr)) {
            matchingDates.push(dateStr);
          }
        }
        current.setDate(current.getDate() + 1);
      }
      return matchingDates;
    } else if (recurringRule.frequency === 'weekly' && recurringRule.day) {
      // Weekly BLOCK booking - find dates matching the day of week
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const targetDayIndex = dayNames.indexOf(recurringRule.day);
      
      if (targetDayIndex === -1) {
        console.warn(`Invalid day name in recurringRule: ${recurringRule.day}`);
        return [];
      }

      // Get the start date from the recurring rule (when the block booking started)
      const ruleStartDate = recurringRule.startDate ? new Date(recurringRule.startDate + 'T00:00:00') : start;

      const current = new Date(start);
      
      while (current <= end) {
        // Only include dates that match the day of week AND are on or after the rule start date
        if (current.getDay() === targetDayIndex) {
          const currentDateOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate());
          const ruleStartDateOnly = new Date(ruleStartDate.getFullYear(), ruleStartDate.getMonth(), ruleStartDate.getDate());
          
          if (currentDateOnly >= ruleStartDateOnly) {
            const dateStr = formatDateToString(current);
            // Exclude dates in exceptions
            if (!exceptionSet.has(dateStr)) {
              matchingDates.push(dateStr);
            }
          }
        }
        current.setDate(current.getDate() + 1);
      }
      
      return matchingDates;
    }

    return [];
  };

  // Helper function to check if two time ranges overlap
  const timeRangesOverlap = (start1, end1, start2, end2) => {
    // Convert time strings (HH:MM) to minutes for easier comparison
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const start1Min = timeToMinutes(start1);
    const end1Min = timeToMinutes(end1);
    const start2Min = timeToMinutes(start2);
    const end2Min = timeToMinutes(end2);

    // Check if ranges overlap (they overlap if one starts before the other ends)
    return start1Min < end2Min && start2Min < end1Min;
  };

  // Helper function to split a time range into non-overlapping segments
  const splitTimeRange = (blockStart, blockEnd, regularBookings) => {
    // Convert time strings to minutes
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const minutesToTime = (minutes) => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    const blockStartMin = timeToMinutes(blockStart);
    const blockEndMin = timeToMinutes(blockEnd);

    // Get all regular booking time ranges and sort by start time
    const regularRanges = regularBookings
      .map(b => ({
        start: timeToMinutes(b.startTime),
        end: timeToMinutes(b.endTime)
      }))
      .sort((a, b) => a.start - b.start);

    // Find non-overlapping segments
    const segments = [];
    let currentStart = blockStartMin;

    for (const regularRange of regularRanges) {
      // If regular booking starts after current segment start, add segment before it
      if (regularRange.start > currentStart) {
        segments.push({
          start: minutesToTime(currentStart),
          end: minutesToTime(regularRange.start)
        });
      }
      // Update current start to after the regular booking ends
      currentStart = Math.max(currentStart, regularRange.end);
    }

    // Add final segment if there's remaining time
    if (currentStart < blockEndMin) {
      segments.push({
        start: minutesToTime(currentStart),
        end: minutesToTime(blockEndMin)
      });
    }

    return segments;
  };

  // Helper function to expand BLOCK bookings and merge with regular bookings
  const processBookingsWithBlocks = (bookings, startDate, endDate) => {
    // Separate BLOCK bookings from regular bookings
    const blockBookings = bookings.filter(b => b.bookingDate === 'BLOCK');
    const regularBookings = bookings.filter(b => b.bookingDate !== 'BLOCK');

    // Create a map of regular bookings grouped by date and court for conflict checking
    const regularBookingsByDateAndCourt = new Map();
    regularBookings.forEach(booking => {
      const key = `${booking.bookingDate}#${booking.court}`;
      if (!regularBookingsByDateAndCourt.has(key)) {
        regularBookingsByDateAndCourt.set(key, []);
      }
      regularBookingsByDateAndCourt.get(key).push(booking);
    });

    // Expand BLOCK bookings
    const expandedBlockBookings = [];
    blockBookings.forEach(blockBooking => {
      if (!blockBooking.isRecurring || !blockBooking.recurringRule) return;

      let recurringRule;
      try {
        recurringRule = typeof blockBooking.recurringRule === 'string' 
          ? JSON.parse(blockBooking.recurringRule) 
          : blockBooking.recurringRule;
      } catch (e) {
        console.error(`Error parsing recurringRule for BLOCK booking ${blockBooking.bookingId}:`, e);
        return;
      }

      // Calculate which dates this BLOCK booking applies to (pass court for per-court exceptions)
      const applicableDates = calculateBlockBookingDates(recurringRule, startDate, endDate, blockBooking.court);
      
      // Debug logging for Date view
      if (startDate === endDate) {
        console.log('Date view - BLOCK booking processing:', {
          blockBookingId: blockBooking.bookingId,
          recurringRule,
          startDate,
          endDate,
          applicableDates
        });
      }

      // Create expanded bookings for each applicable date
      applicableDates.forEach(applicableDate => {
        const key = `${applicableDate}#${blockBooking.court}`;
        const regularBookingsOnSameDateAndCourt = regularBookingsByDateAndCourt.get(key) || [];
        
        // Check if any regular booking overlaps with the BLOCK booking time range
        const hasOverlap = regularBookingsOnSameDateAndCourt.some(regularBooking => {
          return timeRangesOverlap(
            blockBooking.startTime,
            blockBooking.endTime,
            regularBooking.startTime,
            regularBooking.endTime
          );
        });
        
        if (hasOverlap) {
          // Split the BLOCK booking into non-overlapping segments
          const segments = splitTimeRange(
            blockBooking.startTime,
            blockBooking.endTime,
            regularBookingsOnSameDateAndCourt
          );
          
          // Create a booking for each non-overlapping segment
          segments.forEach(segment => {
            expandedBlockBookings.push({
              ...blockBooking,
              bookingDate: applicableDate,
              startTime: segment.start,
              endTime: segment.end
            });
          });
        } else {
          // No overlap, add the full BLOCK booking
          expandedBlockBookings.push({
            ...blockBooking,
            bookingDate: applicableDate
          });
        }
      });
    });

    // Merge regular bookings with expanded BLOCK bookings
    return [...regularBookings, ...expandedBlockBookings];
  };

  const fetchBookingsForView = async (date, viewMode) => {
    setIsLoading(true);
    try {
      if (viewMode === 'Date') {
        // Fetch single day data for court view
        const dateStr = formatDateToString(date);
        const rawBookings = await getBookingsInRange(date, date);
        const refreshedToken = rawBookings?._token || null;
        if (refreshedToken) {
          loginUser(refreshedToken);
        }
        const processedBookings = processBookingsWithBlocks(rawBookings, dateStr, dateStr);
        setCourtViewBookings(processedBookings);
        return processedBookings;
      } else {
        // Default to Calendar view - fetch week data
        const startOfWeek = getStartOfWeek(new Date(date));
        const endOfWeek = getEndOfWeek(new Date(startOfWeek), isSmallScreen);
        const rawBookings = await getBookingsInRange(startOfWeek, endOfWeek);
        const refreshedToken = rawBookings?._token || null;
        if (refreshedToken) {
          loginUser(refreshedToken);
        }
        const processedBookings = processBookingsWithBlocks(rawBookings, formatDateToString(startOfWeek), formatDateToString(endOfWeek));
        setCalendarBookings(processedBookings);
        return processedBookings;
      }
    } catch (err) {
      switch (err.type) {
        case ErrorType.NETWORK:
          handleError('Unable to load court availability. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to load court availability. Please try again later.');
          break;
        default:
          handleError('Failed to load court availability. Please try again.');
      }
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareBooking = async (booking) => {
    if (!booking?.bookingId || !booking?.bookingDate) return;

    const params = new URLSearchParams({
      b: booking.bookingId,
      d: booking.bookingDate
    });
    const shareUrl = `${window.location.origin}/dashboard?${params.toString()}`;
    const inviteName = user?.firstName || 'A member';

    try {
      if (navigator.share) {
        await navigator.share({
          //title: 'Join my booking',
          //text: `${inviteName} is inviting you to join a booking`,
          title: `${inviteName} is inviting you to join a booking`,
          url: shareUrl
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        window.alert('Share link copied to clipboard');
        return;
      }

      handleError('Unable to share on this device. Please copy the URL from your browser bar.');
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          window.alert('Share link copied to clipboard');
          return;
        } catch {
          // fall through to generic error
        }
      }
      handleError('Failed to share booking link. Please try again.');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkBookingId = params.get('b');
    const deepLinkBookingDate = params.get('d');

    if (!deepLinkBookingId || !deepLinkBookingDate) {
      return;
    }

    const deepLinkKey = `${deepLinkBookingId}|${deepLinkBookingDate}`;
    if (deepLinkHandledRef.current === deepLinkKey) {
      return;
    }
    deepLinkHandledRef.current = deepLinkKey;

    const parsedDate = new Date(`${deepLinkBookingDate}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
      handleError('Invalid shared booking link.');
      navigate('/dashboard', { replace: true });
      return;
    }

    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const clubNow = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
    const todayDateStr = formatDateToString(clubNow);

    if (deepLinkBookingDate < todayDateStr) {
      handleError('This shared booking link has expired.');
      navigate('/dashboard', { replace: true });
      return;
    }

    const openDeepLinkedBooking = async () => {
      if (viewMode === 'Date') {
        setSelectedDate(parsedDate);
      } else {
        setCurrentDate(parsedDate);
      }

      const loadedBookings = await fetchBookingsForView(parsedDate, viewMode);
      const matchedBooking = (loadedBookings || []).find(
        (booking) => booking.bookingId === deepLinkBookingId && booking.bookingDate === deepLinkBookingDate
      );

      if (matchedBooking) {
        setSelectedBooking(matchedBooking);
        await fetchUserBookings();
      } else {
        handleError('Shared booking was not found or is no longer available.');
      }

      navigate('/dashboard', { replace: true });
    };

    openDeepLinkedBooking();
  }, [location.search, navigate]);

  const handlePreviousWeek = async () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - (isSmallScreen ? 3 : 7));
    setCurrentDate(newDate);
    await fetchBookingsForView(newDate, 'Calendar');
  };

  const handleNextWeek = async () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (isSmallScreen ? 3 : 7));
    setCurrentDate(newDate);
    await fetchBookingsForView(newDate, 'Calendar');
  };

  const handleNextDay = async () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
    await fetchBookingsForView(newDate, 'Date');
  };

  const canNavigateToPreviousDay = (() => {
    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const todayClubTime = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
    const today = new Date(todayClubTime);
    today.setHours(0, 0, 0, 0);

    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return selected > today;
  })();

  const handlePreviousDay = async () => {
    if (!canNavigateToPreviousDay) return;

    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
    await fetchBookingsForView(newDate, 'Date');
  };

  const handleSlotClick = async (slotData) => {
    const { court, date, startTime } = slotData;

    // Get the current time in UTC
    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000)); // Adjust to UTC
    const currentTime = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000)); // Adjust to South Africa time

    const [hour, minute] = startTime.split(':').map(Number);
    const slotTime = new Date(date);
    slotTime.setHours(hour, minute, 0, 0);

    // Check if the slot is in the past (only block truly past slots)
    if (slotTime < currentTime) {
      return; // Do not proceed if the slot is in the past
    }

    // Get the court type
    const courtType = court.split('-')[0]; // Determine court type (eg: tennis or pickleball)
    const courtPermissions = user?.permissions[courtType] || {};
    const advanceBookingLimit = courtPermissions.advanceBookingLimit;

    // Calculate the difference in days between the selected date and the current date
    const selectedDate = new Date(date);
    const currentDate = new Date(currentTime);
    const timeDifference = selectedDate - currentDate; // Difference in milliseconds
    const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)); // Convert to days

    // Check if the booking exceeds the advance booking limit
    if (advanceBookingLimit !== -1 && dayDifference > advanceBookingLimit) {
      handleError(`You are only permitted to book ${advanceBookingLimit} days in advance.`);
      return; // Do not proceed to open the BookingModal
    }

    // Proceed to check for slot availability and open the BookingModal
    try {
      const latestBookings = await getBookingsInRange(date, date);
      const isSlotTaken = latestBookings.some(booking => 
        booking.court === court && 
        booking.bookingDate === date &&
        booking.startTime === startTime
      );

        if (isSlotTaken) {
        handleError('This slot has just been booked by another user. Please select a different slot.');
        await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
        await fetchUserBookings();
        return;
      }

      // Open immediately after availability check; /userBookings runs after submit (or payment close).
      setSelectedSlot(slotData);
      setIsModalOpen(true);
    } catch (err) {
      handleError('Unable to check slot availability. Please check your internet connection.');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSlot(null);
    setSelectedBooking(null);
    setEditingBooking(null);
  };

  const handleBookingSubmit = async (newBooking) => {
    console.log('in Dashboard handleBookingSubmit, newBooking:', newBooking);
    try {
      const startDate = newBooking.bookingDate;
      const endDate = newBooking.bookingDate;
      const latestBookings = await getBookingsInRange(startDate, endDate);
      
      // Check if any of the selected courts are already booked
      const isAnySlotTaken = newBooking.courts.some(court => 
        latestBookings.some(booking => {
          // Must be same court
          if (booking.court !== court) return false;
          
          // Check if both are BLOCK bookings
          const isNewBookingBlock = newBooking.bookingDate === 'BLOCK' && 
                                    newBooking.isRecurring && 
                                    newBooking.recurringRule &&
                                    newBooking.recurringRule.duration === -1;
          const isExistingBookingBlock = booking.bookingDate === 'BLOCK' && 
                                         booking.isRecurring && 
                                         booking.recurringRule;
          
          if (isNewBookingBlock && isExistingBookingBlock) {
            // Both are BLOCK bookings - extract day from SK (format: "court#startTime#day")
            // The SK is stored in booking['court#startTime'] or we can extract from the booking object
            const existingSkParts = (booking['court#startTime'] || `${booking.court}#${booking.startTime}`).split('#');
            const existingDay = existingSkParts[2] || null; // Day is in SK for BLOCK bookings
            
            // Get day for new booking from recurringRule
            let newBookingDay = null;
            if (newBooking.recurringRule) {
              const newRecurringRule = typeof newBooking.recurringRule === 'string' 
                ? JSON.parse(newBooking.recurringRule) 
                : newBooking.recurringRule;
              if (newRecurringRule.frequency === 'weekly' && newRecurringRule.day) {
                newBookingDay = newRecurringRule.day;
              } else if (newRecurringRule.frequency === 'daily') {
                newBookingDay = 'DAILY';
              }
            }
            
            if (newBookingDay && existingDay) {
              if (newBookingDay !== existingDay) {
                // Different days, no conflict
                return false;
              }
            } else if (!newBookingDay || !existingDay) {
              // If either booking doesn't have a day, skip conflict check (shouldn't happen for new BLOCK bookings)
              return false;
            }
          } else if (newBooking.bookingDate !== booking.bookingDate) {
            // Not both BLOCK bookings, and different booking dates - no conflict
            return false;
          }
          
          // Check for time overlap
          return (booking.startTime < newBooking.endTime) && // Existing booking starts before new booking ends
                 (booking.endTime > newBooking.startTime);   // Existing booking ends after new booking starts
        })
      );

      if (isAnySlotTaken) {
        handleModalClose();
        await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
        handleError('One or more of the selected slots has been booked by another user. Please check and try again.');
        return;
      }

      const response = await createBooking(newBooking);
      console.log('**********createBooking response:', response);
      if (response?.token) {
        loginUser(response.token);
      }

      // Calculate total payment amount from all successful bookings
      const totalPaymentAmount = response.successfulBookings.reduce((sum, booking) => {
        return sum + (parseFloat(booking.amountDue ?? booking.bookingFee) || 0);
      }, 0);

      console.log('Total payment amount for Zapper:', totalPaymentAmount);

      if (totalPaymentAmount > 0) {
        const reference = response.paymentReference || `Booking_${response.successfulBookings[0].bookingId}`;
        console.log('Zapper reference:', reference);
        const customText = generateBookingPaymentText(response.successfulBookings);
        setPaymentDetails({
          totalPaymentAmount,
          reference,
          response,
          customText
        });
        
        // need to close bookingModal before opening PaymentModal
        setIsModalOpen(false);
        console.log('************* OPENING PAYMENT MODAL (setShowPaymentModal(true))');
        setShowPaymentModal(true);
      } else {
        // If no payment required, proceed with normal flow
        const isMultiBooking = newBooking.isRecurring || newBooking.courts.length > 1;
        if (isMultiBooking || response.failedBookings.length > 0) {
          setBookingSummary({
            ...response,
            recurringRule: newBooking.recurringRule
          });
          setShowSummaryModal(true);
        }
        await fetchUserBookings();
        await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
        handleModalClose();
      }
    } catch (err) {
      console.error('Error in handleBookingSubmit:', err);
      // Re-throw the error so BookingModal can handle it
      throw err;
    }
  };

  const handlePaymentComplete = async () => {  
    console.log('*********** in handlePaymentComplete');
    if (paymentDetails) {
      try {
        if (paymentDetails.type === 'public_join') {
          // Handle join payment completion
          const statusResponse = await updateBookingStatus({
            joinPayments: [{
              bookingId: paymentDetails.bookingId,
              bookingDate: paymentDetails.bookingDate,
              courtStartTime: paymentDetails.courtStartTime,
              userUuid: paymentDetails.userUuid,
              fee: paymentDetails.fullFee ?? paymentDetails.totalPaymentAmount,
              action: 'move_to_confirmed'
            }]
          });
          if (statusResponse?.token) {
            loginUser(statusResponse.token);
          }
        } else {
          // Handle regular booking payment completion
          const statusResponse = await updateBookingStatus({
            successfulBookings: paymentDetails.response.successfulBookings
          });
          if (statusResponse?.token) {
            loginUser(statusResponse.token);
          }
        }
        //await handleBookingFinalization(); // skipped since this will be done anyway when the user closes the Payment Modal
      } catch (error) {
        // Close payment modal first to prevent multiple QR codes
        setShowPaymentModal(false);
        // Show error modal to user with reference
        handleError(`Failed to update booking status. Please contact the club with Reference: ${paymentDetails.reference}`);
      }
    }

  };

  const handleJoinBooking = async (joinData) => {
    console.log('*********** in handleJoinBooking', joinData);
    
    try {
        if (joinData.fee > 0) {
          // Payment required - add user to pending and open payment modal
          const statusResponse = await updateBookingStatus({
            joinPayments: [{
              bookingDate: joinData.bookingDate,
              courtStartTime: joinData.courtStartTime,
              userUuid: joinData.userUuid,
              fee: joinData.fee,
              action: 'add_pending'
            }]
          });
          if (statusResponse?.token) {
            loginUser(statusResponse.token);
          }
          
          const joinResult = statusResponse?.joinPaymentsResults?.find(result =>
            result.bookingDate === joinData.bookingDate &&
            result.courtStartTime === joinData.courtStartTime &&
            result.userUuid === joinData.userUuid
          );
          const amountDue = joinResult?.amountDue ?? joinData.fee;
          const actionPerformed = joinResult?.actionPerformed ?? 'add_pending';
          console.log('Join payment reference:', joinResult?.paymentReference, `(${joinResult?.paymentReference?.length ?? 0} chars)`);
          
          if (amountDue > 0 && actionPerformed === 'add_pending') {
            // Set up payment details for the payment modal
            setPaymentDetails({
              totalPaymentAmount: amountDue,
              fullFee: joinData.fee,
              reference: joinResult?.paymentReference,
              type: 'public_join',
              bookingId: joinData.bookingId,
              bookingDate: joinData.bookingDate,
              courtStartTime: joinData.courtStartTime,
              userUuid: joinData.userUuid
            });
            
            // Close booking details modal and open payment modal
            setSelectedBooking(null);
            setShowPaymentModal(true);
          } else {
            // Credits covered the fee, join confirmed immediately
            setSelectedBooking(null);
            await fetchUserBookings();
            await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
          }
        } else {
          // No payment required - join directly
          const statusResponse = await updateBookingStatus({
            joinPayments: [{
              bookingDate: joinData.bookingDate,
              courtStartTime: joinData.courtStartTime,
              userUuid: joinData.userUuid,
              fee: 0,
              action: 'add_direct_confirmed'
            }]
          });
          if (statusResponse?.token) {
            loginUser(statusResponse.token);
          }
          
          // Close booking details modal and refresh data
          setSelectedBooking(null);
          await fetchUserBookings();
          await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
        }
    } catch (error) {
      console.error('Error joining booking:', error);
      handleError('Failed to join booking. Please try again.');
    }
  };

  const handlePaymentModalClose = async () => {
    console.log('*********** in handlePaymentModalClose');
    if (paymentDetails) {
      await handleBookingFinalization();
    }
  };

  const handleBookingFinalization = async () => {
    console.log('*********** in handleBookingFinalization');
    // make sure Payment Modal is closed first so we dont' generate a new Zapper Paymetn Widget
    setPaymentDetails(null);
    setShowPaymentModal(false);
    
    if (paymentDetails && paymentDetails.type === 'public_join') {
      // For join payments, refresh both user bookings and calendar data
      await fetchUserBookings();
      await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
    } else if (paymentDetails && paymentDetails.response) {
      // For regular bookings, handle summary modal if needed
      const isMultiBooking = paymentDetails.response.successfulBookings.length > 1;
      if (isMultiBooking || paymentDetails.response.failedBookings.length > 0) {
        setBookingSummary({
          ...paymentDetails.response,
          recurringRule: paymentDetails.response.recurringRule
        });
        setShowSummaryModal(true);
      }
      await fetchUserBookings();
      await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
      // Close Booking Modal
      handleModalClose();
    }
  };

  const handleEditBookingSubmit = async (newBooking) => {
    console.log("*********** isEditing", newBooking.isEditing);
    console.log("*********** editMode", newBooking.editMode);
    console.log("*********** isEditing From", editingBooking);
    console.log("*********** isEditing To", newBooking);

    try {
        const startDate = newBooking.bookingDate;
        const endDate = newBooking.bookingDate;
        const latestBookings = await getBookingsInRange(startDate, endDate);
        
        // Filter out the editingBooking from the latestBookings
        const filteredBookings = latestBookings.filter(booking => 
            !(booking.bookingId === editingBooking.bookingId) // Exclude if bookingId matches
        );

        // Check if any of the selected courts are already booked
        const isAnySlotTaken = newBooking.courts.some(court => 
            filteredBookings.some(booking => {
              // Must be same court
              if (booking.court !== court) return false;
              
              // Check if both are BLOCK bookings
              const isNewBookingBlock = newBooking.bookingDate === 'BLOCK' && 
                                        newBooking.isRecurring && 
                                        newBooking.recurringRule &&
                                        newBooking.recurringRule.duration === -1;
              const isExistingBookingBlock = booking.bookingDate === 'BLOCK' && 
                                             booking.isRecurring && 
                                             booking.recurringRule;
              
              if (isNewBookingBlock && isExistingBookingBlock) {
                // Both are BLOCK bookings - extract day from SK (format: "court#startTime#day")
                const existingSkParts = (booking['court#startTime'] || `${booking.court}#${booking.startTime}`).split('#');
                const existingDay = existingSkParts[2] || null; // Day is in SK for BLOCK bookings
                
                // Get day for new booking from recurringRule
                let newBookingDay = null;
                if (newBooking.recurringRule) {
                  const newRecurringRule = typeof newBooking.recurringRule === 'string' 
                    ? JSON.parse(newBooking.recurringRule) 
                    : newBooking.recurringRule;
                  if (newRecurringRule.frequency === 'weekly' && newRecurringRule.day) {
                    newBookingDay = newRecurringRule.day;
                  } else if (newRecurringRule.frequency === 'daily') {
                    newBookingDay = 'DAILY';
                  }
                }
                
                if (newBookingDay && existingDay) {
                  if (newBookingDay !== existingDay) {
                    // Different days, no conflict
                    return false;
                  }
                } else if (!newBookingDay || !existingDay) {
                  // If either booking doesn't have a day, skip conflict check (shouldn't happen for new BLOCK bookings)
                  return false;
                }
              } else if (newBooking.bookingDate !== booking.bookingDate) {
                // Not both BLOCK bookings, and different booking dates - no conflict
                return false;
              }
              
              // Check for time overlap
              return (booking.startTime < newBooking.endTime) && // Existing booking starts before new booking ends
                     (booking.endTime > newBooking.startTime);   // Existing booking ends after new booking starts
            })
        );

        if (isAnySlotTaken) {
            handleModalClose();
            await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
            handleError('One or more of the selected slots has been booked by another user. Please check and try again.');
            return;
        }

        // Calculate refund amount from confirmed old bookings
        let oldBookings;
        if (newBooking.editMode === 'individual') {
          // For individual edit, only refund the specific booking being edited
          oldBookings = [editingBooking];
        } else {
          // For full edit, refund all bookings in the group for the same day
          const groupedBookings = recurringGroups[editingBooking.bookingId] || [editingBooking];
          // Filter to only include bookings for the same day as the one being edited
          oldBookings = groupedBookings.filter(booking => 
            booking.bookingDate === editingBooking.bookingDate && 
            booking.startTime === editingBooking.startTime &&
            booking.endTime === editingBooking.endTime
          );
        }

        // Check if any of the old bookings have joiners
        const hasJoiners = oldBookings.some(booking => checkBookingHasJoiners(booking));
        
        // If this is a full edit with joiners, skip the deletion step as the backend will handle it
        if (newBooking.editMode === 'full' && hasJoiners) {
          console.log('Skipping frontend deletion for multi-court booking with joiners - backend will handle deletion');
        } else {
          // For individual edits or bookings without joiners, proceed with normal deletion
          console.log('Proceeding with normal deletion flow');
        }
        
        const refundAmount = oldBookings.reduce((sum, booking) => {
            if (booking.status === 'confirmed') {
                return sum + (parseFloat(booking.bookingFee) || 0);
            }
            return sum;
        }, 0);

        let didDeleteOldBookingsInFrontend = false;

        // Proceed with deleting the old booking and creating the new booking
        if (newBooking.editMode === 'individual') {
          // For individual edit mode, skip deletion - the backend will handle it internally
          console.log('Individual edit mode: skipping deletion, backend will handle internally');
        } else if (newBooking.editMode === 'full' && hasJoiners) {
          // For full edit mode with joiners, skip deletion - the backend will handle it internally
          console.log('Full edit mode with joiners: skipping deletion, backend will handle internally');
        } else {
          // For full edit mode without joiners, delete the old bookings first
          console.log('Full edit mode without joiners: proceeding with deletion');
          await confirmDelete(editingBooking);
          didDeleteOldBookingsInFrontend = true;
        }
        const response = await createBooking(newBooking);
        console.log('********* createBooking response', response);
        if (response?.token) {
          loginUser(response.token);
        }

        // Calculate total payment amount from new bookings
        const totalPaymentAmount = response.successfulBookings.reduce((sum, booking) => {
            return sum + (parseFloat(booking.amountDue ?? booking.bookingFee) || 0);
        }, 0);

        // Only apply legacy refund offset when the old booking was NOT deleted in the frontend.
        // If we deleted via confirmDelete, credits were already refunded and included in amountDue.
        const refundOffset = didDeleteOldBookingsInFrontend ? 0 : refundAmount;
        const netPaymentAmount = Math.max(0, totalPaymentAmount - refundOffset);
        console.log('Edit payment totals:', { totalPaymentAmount, refundOffset, netPaymentAmount, didDeleteOldBookingsInFrontend });

        if (netPaymentAmount > 0) {
            const reference = response.paymentReference || `Booking_${response.successfulBookings[0].bookingId}`;
            console.log('Zapper reference:', reference);
            const customText = generateBookingPaymentText(response.successfulBookings);
            setPaymentDetails({
                totalPaymentAmount: netPaymentAmount,
                reference,
                response,
                customText
            });
            
            // need to close bookingModal before opening PaymentModal
            setIsModalOpen(false);
            console.log('************* OPENING PAYMENT MODAL (setShowPaymentModal(true))');
            setShowPaymentModal(true);
        } else {
            // If amountDue still exists but was fully offset by legacy refund calculation,
            // confirm pending bookings now to preserve existing individual/joiner edit behavior.
            if (totalPaymentAmount > 0) {
              const statusResponse = await updateBookingStatus({
                successfulBookings: response.successfulBookings
              });
              if (statusResponse?.token) {
                loginUser(statusResponse.token);
              }
            }
            
            // Handle multi-booking scenario and summary modal
            const isMultiBooking = newBooking.isRecurring || newBooking.courts.length > 1;
            if (isMultiBooking || response.failedBookings.length > 0) {
                setBookingSummary({
                    ...response,
                    recurringRule: newBooking.recurringRule
                });
                setShowSummaryModal(true);
            }

            await fetchUserBookings();
            await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
            handleModalClose();
        }
    } catch (err) {
        console.error('Error in handleEditBookingSubmit:', err);
        // Re-throw the error so BookingModal can handle it
        throw err;
    }
};

  const handleDeleteBooking = async (booking) => {
    // Check if this is a joined booking (user is actually a joiner in the booking)
    let isJoinedBooking = false;
    
    if (booking.public) {
      try {
        const publicInfo = typeof booking.public === 'string' ? JSON.parse(booking.public) : booking.public;
        // Check if user is in pending or confirmed lists
        isJoinedBooking = (publicInfo.pending && publicInfo.pending[user.uuid]) || 
                         (publicInfo.confirmed && publicInfo.confirmed[user.uuid] !== undefined);
      } catch (error) {
        console.error('Error parsing public booking info:', error);
        // Fallback to original logic if parsing fails
        isJoinedBooking = booking.createdBy !== user.uuid;
      }
    }
    
    if (isJoinedBooking) {
      // For joined bookings, show leave confirmation
      setDeleteConfirmation({
        isOpen: true,
        booking,
        isJoinedBooking: true
      });
    } else {
      // Check if this is a BLOCK booking
      let isBlockBooking = false;
      if (booking.isRecurring && booking.recurringRule) {
        try {
          const recurringRule = typeof booking.recurringRule === 'string' 
            ? JSON.parse(booking.recurringRule) 
            : booking.recurringRule;
          isBlockBooking = recurringRule.duration === -1;
        } catch (e) {
          // If we can't parse, assume it's not a BLOCK booking
        }
      }
      
      // For BLOCK bookings, fetch the original booking data if not already stored
      let originalBlockBooking = booking._originalBlockBooking;
      let allBlockBookings = booking._allBlockBookings;
      
      if (isBlockBooking && booking.bookingId && !originalBlockBooking) {
        try {
          // Fetch bookings for today (or any date) - the backend always includes BLOCK bookings
          const today = formatDateToString(new Date());
          const allBookings = await getBookingsInRange(today, today);
          
          // Find the BLOCK booking by bookingId and court
          const blockBookings = allBookings.filter(b => b.bookingDate === 'BLOCK');
          originalBlockBooking = blockBookings.find(b => 
            b.bookingId === booking.bookingId && 
            b.court === booking.court
          );
          
          // Get all BLOCK bookings with the same bookingId (for multi-court support)
          if (originalBlockBooking) {
            allBlockBookings = blockBookings.filter(b => 
              b.bookingId === booking.bookingId
            );
          }
        } catch (error) {
          console.error('Error fetching original BLOCK booking:', error);
          handleError('Unable to fetch BLOCK booking data. Please try again.');
          return;
        }
        
        if (!originalBlockBooking) {
          handleError('Unable to delete BLOCK booking: missing original booking data. Please try again.');
          return;
        }
      }
      
      // For regular bookings, use existing logic
      const groupedBookings = recurringGroups[booking.bookingId] || [];
      const uniqueCourts = getUniqueCourts(groupedBookings);
      
      // For BLOCK bookings, store the original booking data to avoid refetching
      const deleteConfirmationData = {
        isOpen: true,
        booking,
        isRecurring: booking.isRecurring,
        bookingId: booking.bookingId,
        isMultiCourt: uniqueCourts.length > 1,
        isJoinedBooking: false
      };
      
      // Store the original BLOCK booking data if available
      if (originalBlockBooking) {
        deleteConfirmationData.originalBlockBooking = originalBlockBooking;
        if (allBlockBookings) {
          deleteConfirmationData.allBlockBookings = allBlockBookings;
        }
      }
      
      setDeleteConfirmation(deleteConfirmationData);
    }
    
    // Close the BookingDetailsModal here
    setSelectedBooking(null);
  };

  const confirmDelete = async (booking) => {
    try {
      console.log('CONFIRM DELETE FOR BOOKING:', booking);
      
      // Check if this is a joined booking
      if (deleteConfirmation.isJoinedBooking) {
        // For joined bookings, remove user from public booking
        const courtStartTime = `${booking.court}#${booking.startTime}`;
        const statusResponse = await updateBookingStatus({
          joinPayments: [{
            bookingDate: booking.bookingDate,
            courtStartTime: courtStartTime,
            userUuid: user.uuid,
            fee: 0,
            action: 'remove_user'
          }]
        });
        if (statusResponse?.token) {
          loginUser(statusResponse.token);
        }
        
        // Check if user is the creator - if so, don't remove from local state
        const isCreator = booking.createdBy === user.uuid;
        
        if (!isCreator) {
          // Remove from local state - for multi-court bookings, remove all courts in the same group
          console.log('Booking object for leave operation:', booking);
          console.log('Booking ID:', booking.bookingId);
          
          if (booking.bookingId) {
            // Multi-court booking - remove all courts with the same bookingId
            console.log('Removing all courts with bookingId:', booking.bookingId);
            setUserBookings(prevBookings => 
              prevBookings.filter(b => b.bookingId !== booking.bookingId)
            );
            setCalendarBookings(prevBookings => 
              prevBookings.filter(b => b.bookingId !== booking.bookingId)
            );
          } else {
            // Single court booking - remove specific court
            console.log('No bookingId found, removing single court');
            const key = `${booking.bookingDate}#${booking.court}#${booking.startTime}`;
            setUserBookings(prevBookings => 
              prevBookings.filter(b => `${b.bookingDate}#${b.court}#${b.startTime}` !== key)
            );
            setCalendarBookings(prevBookings => 
              prevBookings.filter(b => `${b.bookingDate}#${b.court}#${b.startTime}` !== key)
            );
          }
        } else {
          // For creators, just refresh the data to update the joiner status
          console.log('Creator leaving their own booking - refreshing data to update joiner status');
          await fetchUserBookings();
          await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
        }
      } else {
        // Check if this is a BLOCK booking
        let isBlockBooking = false;
        let blockRecurringRule = null;
        if (booking.isRecurring && booking.recurringRule) {
          try {
            blockRecurringRule = typeof booking.recurringRule === 'string' 
              ? JSON.parse(booking.recurringRule) 
              : booking.recurringRule;
            isBlockBooking = blockRecurringRule.duration === -1;
          } catch (e) {
            // If we can't parse, assume it's not a BLOCK booking
          }
        }

        if (isBlockBooking) {
          // Get the current bookingId (may have changed if booking was recreated after single deletion)
          const currentBookingId = booking.bookingId || deleteConfirmation.booking?.bookingId;
          
          if (!currentBookingId) {
            console.error('No bookingId found for BLOCK booking deletion');
            handleError('Unable to delete BLOCK booking: missing booking ID');
            return;
          }
          
          // Get the original BLOCK booking data (stored when booking was selected, or from deleteConfirmation)
          const originalBlockBooking = booking._originalBlockBooking || 
                                      deleteConfirmation.booking?._originalBlockBooking ||
                                      deleteConfirmation.originalBlockBooking;
          
          if (!originalBlockBooking) {
            console.error('No original BLOCK booking data found');
            handleError('Unable to delete BLOCK booking: missing original booking data. Please try again.');
            return;
          }
          
          // Determine if this is "delete all" or "delete single"
          // For "delete all", onDeleteSeries is called which passes deleteConfirmation.booking directly
          // For "delete single", onConfirm is called which passes bookingToDelete with _isSingleDeletion flag
          const isDeleteAll = !booking._isSingleDeletion && booking === deleteConfirmation.booking;
          
          if (isDeleteAll) {
            // Delete all - delete the BLOCK booking for all courts
            // Use recurringGroups with CURRENT bookingId (may have changed if booking was recreated)
            const groupedBookings = recurringGroups[currentBookingId] || [];
            
            // Extract unique courts from the grouped bookings
            // All bookings in the group have the same bookingId, so we can get all courts
            const uniqueCourts = [...new Set(groupedBookings.map(b => b.court))];
            
            // If we don't have grouped bookings, try to get all courts from the original booking data
            // This handles the case where the booking was recreated and we need to delete all courts
            let courtsToDelete = uniqueCourts;
            if (courtsToDelete.length === 0) {
              // Fallback: use allBlockBookings if available, or just the original court
              const allBlockBookings = deleteConfirmation.allBlockBookings || 
                                      booking._allBlockBookings;
              if (allBlockBookings && allBlockBookings.length > 0) {
                courtsToDelete = [...new Set(allBlockBookings.map(b => b.court))];
              } else {
                courtsToDelete = [originalBlockBooking.court];
              }
            }
            
            // Create bookingKeys for all courts using the original start time and day
            // All courts in a BLOCK booking have the same start time
            // Extract day from recurringRule for SK format
            let blockDay = null;
            if (blockRecurringRule) {
              if (blockRecurringRule.frequency === 'weekly' && blockRecurringRule.day) {
                blockDay = blockRecurringRule.day;
              } else if (blockRecurringRule.frequency === 'daily') {
                blockDay = 'DAILY';
              }
            }
            const bookingKeys = courtsToDelete.map(court => ({
              bookingDate: 'BLOCK',
              courtStartTime: blockDay ? `${court}#${originalBlockBooking.startTime}#${blockDay}` : `${court}#${originalBlockBooking.startTime}`
            }));
            
            console.log('Deleting all BLOCK bookings for bookingId:', currentBookingId, 'courts:', courtsToDelete);
            
            // Delete all BLOCK bookings for all courts
            const deleteResponse = await deleteBooking({
              bookingDate: 'BLOCK',
              court: originalBlockBooking.court, // Use first court for the main call
              startTime: originalBlockBooking.startTime, // Use original start time, not segment time
              bookingId: currentBookingId,
              bookingKeys: bookingKeys // Pass all keys for all courts
            });
            if (deleteResponse?.token) {
              loginUser(deleteResponse.token);
            }
            
            // Remove from local state
            setUserBookings(prevBookings => 
              prevBookings.filter(b => b.bookingId !== currentBookingId)
            );
            setCalendarBookings(prevBookings => 
              prevBookings.filter(b => b.bookingId !== currentBookingId)
            );
          } else {
            // Delete single - add date to exceptions for the selected court only
            // We need to delete all courts and recreate them together (same bookingId), 
            // but store exceptions per-court in the recurringRule
            const dateToExclude = booking.bookingDate; // This is the expanded date
            const selectedCourt = booking.court; // The court for which we're deleting the single instance
            
            console.log('Deleting single BLOCK booking instance:', {
              currentBookingId,
              dateToExclude,
              court: selectedCourt
            });
            
            // Get all courts for this BLOCK booking (from recurringGroups or stored data)
            const groupedBookings = recurringGroups[currentBookingId] || [];
            const allCourts = groupedBookings.length > 0 
              ? [...new Set(groupedBookings.map(b => b.court))]
              : (deleteConfirmation.allBlockBookings || booking._allBlockBookings || [originalBlockBooking])
                  .map(b => b.court)
                  .filter((court, index, self) => self.indexOf(court) === index);
            
            // Get existing exceptions per court (if stored as object) or convert from array format
            let exceptionsByCourt = {};
            if (blockRecurringRule.exceptions) {
              // Check if exceptions is an object (per-court) or array (legacy format)
              if (Array.isArray(blockRecurringRule.exceptions)) {
                // Legacy format: exceptions apply to all courts, convert to per-court format
                allCourts.forEach(court => {
                  exceptionsByCourt[court] = [...blockRecurringRule.exceptions];
                });
              } else {
                // Already in per-court format
                exceptionsByCourt = { ...blockRecurringRule.exceptions };
              }
            }
            
            // Initialize exceptions for each court if not present
            allCourts.forEach(court => {
              if (!exceptionsByCourt[court]) {
                exceptionsByCourt[court] = [];
              }
            });
            
            // Add exception only for the selected court
            if (!exceptionsByCourt[selectedCourt].includes(dateToExclude)) {
              exceptionsByCourt[selectedCourt].push(dateToExclude);
            }
            
            // Update the recurringRule with per-court exceptions
            const updatedRecurringRule = {
              ...blockRecurringRule,
              exceptions: exceptionsByCourt // Store as object with court as key
            };
            
            console.log('Updated recurringRule with per-court exceptions:', updatedRecurringRule);
            
            // Create bookingKeys for all courts (need to delete all courts before recreating)
            // Extract day from recurringRule for SK format
            let blockDay = null;
            if (blockRecurringRule) {
              if (blockRecurringRule.frequency === 'weekly' && blockRecurringRule.day) {
                blockDay = blockRecurringRule.day;
              } else if (blockRecurringRule.frequency === 'daily') {
                blockDay = 'DAILY';
              }
            }
            const bookingKeysToDelete = allCourts.map(court => ({
              bookingDate: 'BLOCK',
              courtStartTime: blockDay ? `${court}#${originalBlockBooking.startTime}#${blockDay}` : `${court}#${originalBlockBooking.startTime}`
            }));
            
            // Delete all BLOCK bookings for all courts (using current bookingId)
            const deleteResponse = await deleteBooking({
              bookingDate: 'BLOCK',
              court: originalBlockBooking.court,
              startTime: originalBlockBooking.startTime, // Use original start time, not segment time
              bookingId: currentBookingId,
              bookingKeys: bookingKeysToDelete // Delete all courts
            });
            if (deleteResponse?.token) {
              loginUser(deleteResponse.token);
            }
            
            // Create a new BLOCK booking with all courts (they'll share the same bookingId)
            // The recurringRule contains per-court exceptions
            // Parse public data if it's already a JSON string to prevent double-encoding
            let publicData = null;
            if (originalBlockBooking.public) {
              try {
                publicData = typeof originalBlockBooking.public === 'string' 
                  ? JSON.parse(originalBlockBooking.public) 
                  : originalBlockBooking.public;
              } catch (e) {
                console.error('Error parsing public data:', e);
                publicData = originalBlockBooking.public;
              }
            }
            
            const categoryId = getCategoryIdForCourtAndName(
              originalBlockBooking.court,
              originalBlockBooking.category || ''
            );
            if (!categoryId) {
              handleError('Could not resolve booking category for this BLOCK booking.');
              return;
            }

            const newBlockBooking = {
              bookingDate: 'BLOCK',
              courts: allCourts, // All courts together - they'll share the same bookingId
              startTime: originalBlockBooking.startTime,
              endTime: originalBlockBooking.endTime,
              categoryId,
              comments: originalBlockBooking.comments || '',
              isRecurring: true,
              recurringRule: updatedRecurringRule, // Contains per-court exceptions
              displayLabel: originalBlockBooking.displayLabel || '',
              public: publicData,
              isEditing: false
            };
            
            await createBooking(newBlockBooking);
            
            console.log('Deleted and recreated BLOCK booking for all courts with per-court exceptions. Selected court:', selectedCourt, 'all courts:', allCourts);
            
            // Refresh data to reflect the updated exceptions
            await fetchUserBookings();
          }
        } else {
          // For regular bookings, calculate all booking keys and use optimized delete
          const { keys: bookingKeys, excludedBookings } = calculateBookingKeys(booking, recurringGroups);
          
          if (bookingKeys.length > 0) {
            const deleteResponse = await deleteBooking({
              bookingDate: booking.bookingDate,
              court: booking.court,
              startTime: booking.startTime,
              bookingId: booking.bookingId,
              bookingKeys: bookingKeys // Pass calculated keys to avoid table scan
            });
            if (deleteResponse?.token) {
              loginUser(deleteResponse.token);
            }
          }
        
          // Show deletion summary modal if there were excluded bookings
          if (excludedBookings.length > 0) {
            setDeletionSummary({
              successfulDeletions: bookingKeys.map(k => {
                const [court, startTime] = k.courtStartTime.split('#');
                return {
                  court: court,
                  date: k.bookingDate,
                  startTime: startTime,
                  endTime: booking.endTime // Use the original booking's end time
                };
              }),
              failedDeletions: excludedBookings.map(b => ({
                court: b.court,
                date: b.bookingDate,
                startTime: b.startTime,
                endTime: b.endTime
              }))
            });
            setShowDeletionSummaryModal(true);
          }

          // Update local state - only remove bookings that were actually deleted
          // Only update local state if there were successful deletions
          if (bookingKeys.length > 0) {
            if (booking.bookingId) {
              // For grouped bookings, only remove the ones that were successfully deleted
              const deletedKeys = new Set(bookingKeys.map(k => `${k.bookingDate}#${k.courtStartTime.split('#')[0]}#${k.courtStartTime.split('#')[1]}`));
              
              setUserBookings(prevBookings => 
                prevBookings.filter(b => {
                  const key = `${b.bookingDate}#${b.court}#${b.startTime}`;
                  return !deletedKeys.has(key);
                })
              );
              setCalendarBookings(prevBookings => 
                prevBookings.filter(b => {
                  const key = `${b.bookingDate}#${b.court}#${b.startTime}`;
                  return !deletedKeys.has(key);
                })
              );
            } else {
              // Remove single booking using bookingDate#courtId#startTime
              const key = `${booking.bookingDate}#${booking.court}#${booking.startTime}`;
              setUserBookings(prevBookings => 
                prevBookings.filter(b => `${b.bookingDate}#${b.court}#${b.startTime}` !== key)
              );
              setCalendarBookings(prevBookings => 
                prevBookings.filter(b => `${b.bookingDate}#${b.court}#${b.startTime}` !== key)
              );
            }
          }
        }
      }
      
      // Refresh current view
      await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
      closeDeleteConfirmation();
    } catch (err) {
      switch (err.type) {
        case ErrorType.VALIDATION:
          handleError(err.message);
          break;
        case ErrorType.NETWORK:
          handleError('Unable to delete booking. Please check your internet connection.');
          break;
        case ErrorType.SERVER:
          handleError('Unable to delete booking. Please try again later.');
          break;
        default:
          handleError('Failed to delete booking. Please try again.');
      }
    }
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      booking: null
    });
  };

  const formatRecurringRule = (rule) => {
    if (!rule) return null;

    try {
      // Handle both old DynamoDB format and new transformed format
      const ruleString = rule.S || rule;
      const parsedRule = typeof ruleString === 'string' ? JSON.parse(ruleString) : ruleString;
      const frequency = parsedRule.frequency;
      
      if (!frequency) return null;
      
      // Check if this is a BLOCK booking (Never Ending)
      //if (parsedRule.duration === -1) {
      //  return 'Never Ending';
      //}
      
      return `${frequency.charAt(0).toUpperCase()}${frequency.slice(1)}`;
    } catch (error) {
      console.error('Error parsing recurring rule:', error);
      return null;
    }
  };

  // Group bookings by bookingId
  const groupBookings = (bookings) => {
    //console.log("in groupBookings, bookings = ",bookings);
    const groups = {};
    bookings.forEach(booking => {
      if (booking.bookingId) {
        if (!groups[booking.bookingId]) {
          groups[booking.bookingId] = [];
        }
        groups[booking.bookingId].push(booking);
      }
    });
    return groups;
  };

  // Sort bookings by date and start time (including minutes)
  const sortBookings = (bookings) => {
    return bookings.sort(compareBookingsByStart);
  };

  // Group bookings and filter out groups with no upcoming bookings
  const groupBookingsWithFiltering = (bookings) => {
    const groups = {};
    bookings.forEach(booking => {
      if (booking.bookingId) {
        if (!groups[booking.bookingId]) {
          groups[booking.bookingId] = [];
        }
        groups[booking.bookingId].push(booking);
      }
    });

    // Filter out groups that have no upcoming bookings
    const filteredGroups = {};
    Object.entries(groups).forEach(([bookingId, groupBookings]) => {
      const now = new Date();
      const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const currentTime = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));

      // Check if any booking in the group hasn't ended yet
      const hasUpcomingBooking = groupBookings.some(booking => {
        const bookingDate = new Date(booking.bookingDate);
        const [endHour, endMinute] = booking.endTime.split(':').map(Number);
        const endTime = new Date(bookingDate);
        endTime.setHours(endHour, endMinute, 0, 0);
        return endTime >= currentTime;
      });

      if (hasUpcomingBooking) {
        filteredGroups[bookingId] = groupBookings;
      }
    });

    return filteredGroups;
  };

  const recurringGroups = groupBookingsWithFiltering(userBookings);
  const nonRecurringBookings = userBookings.filter(booking => !booking.bookingId);
  const sortedNonRecurringBookings = sortBookings(nonRecurringBookings);
  const isDataReady = courtsLoaded && categoriesLoaded;

  const handleSummaryModalClose = () => {
    setShowSummaryModal(false);
    setBookingSummary(null);
  };

  const handleDeletionSummaryModalClose = () => {
    setShowDeletionSummaryModal(false);
    setDeletionSummary(null);
  };

  const onCourtSelect = (court) => {
    setSelectedCourt(court);
  };

  const handleSportChange = (sport) => {
    setSelectedSport(sport);
    savePreferences({ selectedSport: sport });
    
    // Reset to first court of the selected sport or first available court
    if (sport === 'All Courts') {
      setSelectedCourt(courts.length > 0 ? courts[0].court : null);
      // Force Calendar view when All Courts is selected
      setViewMode('Calendar');
      savePreferences({ viewMode: 'Calendar' });
    } else {
      const sportCourts = courts.filter(court => court.court.startsWith(sport));
      setSelectedCourt(sportCourts.length > 0 ? sportCourts[0].court : null);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      setViewMode('Date'); // Switch to Date view when a date is selected
      savePreferences({ viewMode: 'Date' });
      fetchBookingsForView(date, 'Date');
    }
    // If date is null, don't change view mode or fetch bookings (Calendar button case)
  };

  // Function to handle booking selection
  const handleBookingSelect = async (booking) => {
    console.log('**********handleBookingSelect', booking);
    try {
      // Check if this is a BLOCK booking (expanded from a BLOCK booking)
      let isBlockBooking = false;
      if (booking.isRecurring && booking.recurringRule) {
        try {
          const recurringRule = typeof booking.recurringRule === 'string' 
            ? JSON.parse(booking.recurringRule) 
            : booking.recurringRule;
          isBlockBooking = recurringRule.duration === -1;
        } catch (e) {
          // If we can't parse, assume it's not a BLOCK booking
        }
      }

      let latestBooking = null;
      /** True when `fetchBookingsForView` already ran for this click (avoids duplicate GET /calendarBookings). */
      let viewAlreadyRefreshed = false;

      if (isBlockBooking && booking.bookingId) {
        // For BLOCK bookings, fetch bookings for the expanded date
        // The backend always includes BLOCK bookings regardless of date range
        const allBookings = await getBookingsInRange(booking.bookingDate, booking.bookingDate);
        // Find the BLOCK booking by bookingId and court (not by bookingDate since it's 'BLOCK' in DB)
        const blockBookings = allBookings.filter(b => b.bookingDate === 'BLOCK');
        const originalBlockBooking = blockBookings.find(b => 
          b.bookingId === booking.bookingId && 
          b.court === booking.court
        );
        
        // Get all BLOCK bookings with the same bookingId (for multi-court support)
        const allBlockBookingsWithSameId = blockBookings.filter(b => 
          b.bookingId === booking.bookingId
        );
        
        // If found, update it with the expanded date and time for display
        if (originalBlockBooking) {
          // Store the original booking data for later use (e.g., deletion)
          latestBooking = {
            ...originalBlockBooking,
            bookingDate: booking.bookingDate, // Use the expanded date for display
            startTime: booking.startTime,     // Use the segment start time
            endTime: booking.endTime,          // Use the segment end time
            _originalBlockBooking: originalBlockBooking, // Store original for deletion
            _allBlockBookings: allBlockBookingsWithSameId // Store all BLOCK bookings with same bookingId for multi-court deletion
          };
          // Concrete calendar rows for this date (joiners, public counts) live on real-date items,
          // not on the BLOCK template — same payload as /calendarBookings.
          const slotInstance = allBookings.find(
            (b) =>
              b.bookingDate &&
              b.bookingDate !== 'BLOCK' &&
              b.bookingDate === booking.bookingDate &&
              b.court === booking.court &&
              b.startTime === booking.startTime &&
              b.endTime === booking.endTime
          );
          if (slotInstance) {
            latestBooking = {
              ...latestBooking,
              ...(slotInstance.public !== undefined ? { public: slotInstance.public } : {}),
              ...(slotInstance.status !== undefined ? { status: slotInstance.status } : {}),
              ...(slotInstance.joinerNames !== undefined ? { joinerNames: slotInstance.joinerNames } : {}),
            };
          }
        }
      } else {
        // Regular bookings: reuse one calendar fetch when Court (Date) view is already showing this day
        const bookingDateStr = booking.bookingDate;
        const sameDayAsCourtView =
          viewMode === 'Date' && formatDateToString(selectedDate) === bookingDateStr;

        if (sameDayAsCourtView) {
          const processedForDay = await fetchBookingsForView(selectedDate, 'Date');
          viewAlreadyRefreshed = true;
          latestBooking = processedForDay.find(b =>
            b.court === booking.court &&
            b.bookingDate === booking.bookingDate &&
            b.startTime === booking.startTime
          );
        } else {
          const latestBookings = await getBookingsInRange(booking.bookingDate, booking.bookingDate);
          latestBooking = latestBookings.find(b =>
            b.court === booking.court &&
            b.bookingDate === booking.bookingDate &&
            b.startTime === booking.startTime
          );
        }
      }

      if (latestBooking) {
        setSelectedBooking(latestBooking);
      } else {
        // If the booking no longer exists, show an error and refresh the calendar
        handleError('This booking no longer exists. The calendar will be refreshed.');
        //await fetchCalendarBookings(currentDate);
      }
      if (!viewAlreadyRefreshed) {
        await fetchBookingsForView(viewMode === 'Date' ? selectedDate : currentDate, viewMode);
      }
      await fetchUserBookings();
    } catch (err) {
      handleError('Unable to fetch booking details. Please try again.');
    }
  };

  // Function to handle booking editing
  const onEditBooking = (booking, editMode = 'full') => {
    console.log('***********In onEditBooking', booking, 'editMode:', editMode);
    // Create a copy of the booking to modify to slotData expected by BookingModal
    const slotData = { ...booking };
    let isBlockBooking = false;
    let parsedRecurringRule = null;
    if (booking.isRecurring && booking.recurringRule) {
      try {
        parsedRecurringRule = typeof booking.recurringRule === 'string'
          ? JSON.parse(booking.recurringRule)
          : booking.recurringRule;
        isBlockBooking = parsedRecurringRule.duration === -1;
      } catch (e) {
        // ignore parse errors - treat as non-block
      }
    }

    if (editMode === 'individual') {
      // For individual edit mode, only edit the specific booking
      slotData.court = [booking.court];
    } else {
      // For full edit mode, check if there is a bookingId
      if (booking.bookingId) {
          // Retrieve the bookings associated with the bookingId
          const groupedBookings = recurringGroups[booking.bookingId] || [];
          
          // Filter to only include bookings for the same day as the one being edited
          let sameDayBookings = groupedBookings.filter(groupedBooking => 
            groupedBooking.bookingDate === booking.bookingDate && 
            groupedBooking.startTime === booking.startTime &&
            groupedBooking.endTime === booking.endTime
          );
          
          // BLOCK bookings are stored with bookingDate = 'BLOCK' so sameDayBookings may be empty.
          if (sameDayBookings.length === 0 && isBlockBooking) {
            sameDayBookings = booking._allBlockBookings || [];
          }

          // Set the court as an array of all courts for the same day
          const courtsForDay = getUniqueCourts(sameDayBookings);
          slotData.court = courtsForDay.length > 0 ? courtsForDay : [booking.court];

          // Extras are stored on a single court row per day-group. Surface them at
          // the group level so editing any court of a multi-court booking keeps the
          // selected extras (and doesn't lose them if a non-primary court was clicked).
          const parseOptionsList = (raw) => {
            if (!raw) return [];
            try {
              const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
              return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              return [];
            }
          };
          if (parseOptionsList(slotData.options).length === 0) {
            const siblingWithOptions = sameDayBookings.find(
              (sibling) => parseOptionsList(sibling?.options).length > 0
            );
            if (siblingWithOptions) {
              slotData.options = siblingWithOptions.options;
            }
          }
      } else {
          // If no bookingId, set the court as an array with the current court
          slotData.court = [booking.court];
      }
    }

    // Final safety: ensure court exists
    if (!slotData.court || slotData.court.length === 0) {
      slotData.court = [booking.court];
    }

    console.log('**************** slotData.court', slotData.court);
    // Translate bookingDate to date
    // For BLOCK bookings, use 'BLOCK' as the date so BookingModal knows it's a BLOCK booking
    // But preserve the actual booking date for display purposes if this is a materialized instance
    if (isBlockBooking) {
      slotData.date = 'BLOCK';
      // If this is a materialized instance (bookingDate is not 'BLOCK'), preserve it for display
      if (booking.bookingDate && booking.bookingDate !== 'BLOCK') {
        slotData.displayDate = booking.bookingDate;
      }
    } else {
      slotData.date = booking.bookingDate;
    }
    setEditingBooking(slotData); // Set the modified booking for editing
    setEditMode(editMode); // Store the edit mode
    // close BookingDetailsModal and open BookingModal for editing
    setSelectedBooking(null); 
    setIsModalOpen(true); // Open the BookingModal
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setIsErrorModalOpen(true); // Open the ErrorModal
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setError(null); // Clear the error when closing
  };

  return (
    <div className="p-4 bg-slate-100 min-h-screen">
      {/* Only render user bookings once categories are loaded to ensure proper colors */}
      {userBookings.length > 0 && categoriesLoaded && (
        <div>
          <h2 className="text-md md:text-lg font-semibold mb-2 text-slate-800">Your Bookings</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 mb-4">
            {/* Render booking cards */}
            {Object.entries(recurringGroups).map(([bookingId, bookings]) => (
              <BookingGroup
                key={bookingId}
                bookings={bookings}
                bookingId={bookingId}
                onDeleteBooking={handleDeleteBooking}
                onShareBooking={handleShareBooking}
                onBookingSelect={handleBookingSelect}
                isSmallScreen={isSmallScreen}
                formatRecurringRule={formatRecurringRule}
                uniqueSportsCount={availableSports.length}
                user={user}
              />
            ))}
          </div>
        </div>
      )}

      {!isDataReady ? (
        <div className="shadow-md rounded-lg bg-white p-6 flex items-center justify-center text-slate-600">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading courts...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            {availableSports.length > 1 ? (
              // Multiple sports - use flex layout for dropdown + date picker
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <h2 className="text-md md:text-lg font-semibold text-slate-800">Court Availability</h2>
                
                <div className="flex flex-row gap-1 sm:gap-2">
                  {/* Sport Selector */}
                  <select
                    value={selectedSport}
                    onChange={(e) => handleSportChange(e.target.value)}
                    className="block h-8 w-28 sm:w-32 px-3 sm:px-4 py-1 text-slate-800 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition text-sm"
                  >
                    {availableSports.map((sport) => (
                      <option key={sport} value={sport}>
                        {sport === 'All Courts'
                          ? (hasAnyCourtDisplayName ? 'All' : 'All Courts')
                          : formatSportDisplayName(sport)}
                      </option>
                    ))}
                  </select>

                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {/* Previous Day button - only show when in Date view and not today */}
                    {selectedSport !== 'All Courts' && viewMode === 'Date' && canNavigateToPreviousDay && (
                      <Tooltip content="Previous Day">
                        <button
                          onClick={handlePreviousDay}
                          className="p-1 rounded text-slate-800 hover:bg-slate-200 transition"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                      </Tooltip>
                    )}

                    {/* Date Selector - only show when a specific sport is selected */}
                    {selectedSport !== 'All Courts' && (
                      <div className="relative w-32 sm:w-36 min-w-[7rem]">
                        <CustomDatePicker
                          selected={viewMode === 'Date' ? selectedDate : null}
                          onChange={handleDateChange}
                          onCalendarClick={async () => {
                            setViewMode('Calendar');
                            savePreferences({ viewMode: 'Calendar' });
                            // Ensure selectedCourt matches selectedSport when switching to Calendar view
                            if (selectedSport !== 'All Courts') {
                              const sportCourts = courts.filter(court => court.court.startsWith(selectedSport));
                              if (sportCourts.length > 0) {
                                setSelectedCourt(sportCourts[0].court);
                              }
                            } else {
                              setSelectedCourt(courts.length > 0 ? courts[0].court : null);
                            }
                            // Always reset to today when switching to Calendar view
                            const now = new Date();
                            const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
                            const today = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
                            const weekStart = getStartOfWeek(today);
                            setCurrentDate(weekStart);
                            // Fetch bookings for the calendar view date range
                            await fetchBookingsForView(weekStart, 'Calendar');
                          }}
                          minDate={new Date()}
                          placeholderText="Calendar View"
                        />
                      </div>
                    )}

                    {/* Next Day button - only show when a specific sport is selected and in Date view */}
                    {selectedSport !== 'All Courts' && viewMode === 'Date' && (
                      <Tooltip content="Next Day">
                        <button
                          onClick={handleNextDay}
                          className="p-1 rounded text-slate-800 hover:bg-slate-200 transition"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Single sport - inline layout with header and date picker
              <div className="flex items-center gap-1 sm:gap-2 mb-2">
                <h2 className="text-md md:text-lg font-semibold text-slate-800">Court Availability</h2>
                <div className="flex items-center gap-0.5 sm:gap-1">
                  {/* Previous Day button - only show in Date view and not today */}
                  {viewMode === 'Date' && canNavigateToPreviousDay && (
                    <Tooltip content="Previous Day">
                      <button
                        onClick={handlePreviousDay}
                        className="p-1 rounded text-slate-800 hover:bg-slate-200 transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    </Tooltip>
                  )}
                  <div className="relative w-32 sm:w-36 min-w-[7rem]">
                    <CustomDatePicker
                      selected={viewMode === 'Date' ? selectedDate : null}
                      onChange={handleDateChange}
                      onCalendarClick={async () => {
                        setViewMode('Calendar');
                        savePreferences({ viewMode: 'Calendar' });
                        // Ensure selectedCourt is set (for single sport, should already be set, but ensure it)
                        if (!selectedCourt && courts.length > 0) {
                          setSelectedCourt(courts[0].court);
                        }
                        // Always reset to today when switching to Calendar view
                        const now = new Date();
                        const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
                        const today = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
                        const weekStart = getStartOfWeek(today);
                        setCurrentDate(weekStart);
                        // Fetch bookings for the calendar view date range
                        await fetchBookingsForView(weekStart, 'Calendar');
                      }}
                      minDate={new Date()}
                      placeholderText="Calendar View"
                    />
                  </div>
                  {/* Next Day button - only show in Date view */}
                  {viewMode === 'Date' && (
                    <Tooltip content="Next Day">
                      <button
                        onClick={handleNextDay}
                        className="p-1 rounded text-slate-800 hover:bg-slate-200 transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}
          </div>

          {viewMode === 'Calendar' ? (
              <Calendar 
              bookings={calendarBookings}
              selectedCourt={selectedCourt}
              onSlotClick={handleSlotClick}
              currentDate={currentDate}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              isLoading={isLoading}
              onCourtSelect={onCourtSelect}
              onBookingSelect={handleBookingSelect}
              courts={courts}
              localTimeSlots={initialSlotsLoaded ? localTimeSlots[selectedCourt] : []}
                selectedSport={selectedSport}
              user={user}
              onShareBooking={handleShareBooking}
            />
          ) : (
            <CourtView
              bookings={courtViewBookings}
              selectedSport={selectedSport}
              onSlotClick={handleSlotClick}
              selectedDate={selectedDate}
              isLoading={isLoading}
              onBookingSelect={handleBookingSelect}
              courts={courts}
              localTimeSlots={localTimeSlots}
              user={user}
              onShareBooking={handleShareBooking}
              onNextDay={handleNextDay}
              onPreviousDay={handlePreviousDay}
              canGoPreviousDay={canNavigateToPreviousDay}
            />
          )}
        </>
      )}

      {isModalOpen && editingBooking && (
        <BookingModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleEditBookingSubmit}
          slotData={editingBooking}
          isEditing={true}
          courts={courts}
          uniqueSportsCount={getUniqueSports(courts).length}
          editMode={editMode}
          existingBookings={viewMode === 'Calendar' ? calendarBookings : courtViewBookings}
        />
      )}

      {isModalOpen && selectedSlot && !editingBooking && (
        <BookingModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleBookingSubmit}
          slotData={selectedSlot}
          isEditing={false}
          courts={courts}
          uniqueSportsCount={getUniqueSports(courts).length}
          existingBookings={viewMode === 'Calendar' ? calendarBookings : courtViewBookings}
        />
      )}
      
      <BookingSummaryModal
        isOpen={showSummaryModal}
        onClose={handleSummaryModalClose}
        summary={bookingSummary}
      />

      <DeletionSummaryModal
        isOpen={showDeletionSummaryModal}
        onClose={handleDeletionSummaryModalClose}
        summary={deletionSummary}
      />

      <BookingDetailsModal
        isOpen={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        selectedBooking={selectedBooking}
        isSmallScreen={isSmallScreen}
        onDeleteBooking={handleDeleteBooking}
        onEditBooking={onEditBooking}
        onJoinBooking={handleJoinBooking}
        onShareBooking={handleShareBooking}
        uniqueSportsCount={getUniqueSports(courts).length}
        updateBookingStatus={updateBookingStatus}
        setPaymentDetails={setPaymentDetails}
        setShowPaymentModal={setShowPaymentModal}
        setSelectedBooking={setSelectedBooking}
        handleError={handleError}
        recurringGroups={recurringGroups}
        viewBookings={viewMode === 'Calendar' ? calendarBookings : courtViewBookings}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => {
          closeDeleteConfirmation();
          setSelectedBooking(null); // Close the BookingDetailsModal when the ConfirmationModal is closed
        }}
        onConfirm={() => {
          const bookingToDelete = { ...deleteConfirmation.booking }; // Create a copy of the booking
          // Only remove bookingId for regular bookings, not for joined bookings or BLOCK bookings
          // BLOCK bookings need bookingId to be preserved so we can update the exceptions
          if (!deleteConfirmation.isJoinedBooking) {
            // Check if this is a BLOCK booking
            let isBlockBooking = false;
            if (bookingToDelete.isRecurring && bookingToDelete.recurringRule) {
              try {
                const recurringRule = typeof bookingToDelete.recurringRule === 'string' 
                  ? JSON.parse(bookingToDelete.recurringRule) 
                  : bookingToDelete.recurringRule;
                isBlockBooking = recurringRule.duration === -1;
              } catch (e) {
                // If we can't parse, assume it's not a BLOCK booking
              }
            }
            
            // Only remove bookingId for non-BLOCK bookings
            if (!isBlockBooking) {
              delete bookingToDelete.bookingId; // Remove the bookingId to delete the individual instance
            } else {
              // For BLOCK bookings, mark this as a single deletion
              bookingToDelete._isSingleDeletion = true;
            }
          }
          confirmDelete(bookingToDelete); // Pass the modified booking to confirmDelete
        }}
        onDeleteSeries={() => confirmDelete(deleteConfirmation.booking)}
        title={
          deleteConfirmation.isJoinedBooking 
            ? "Leave Booking" 
            : (deleteConfirmation.isRecurring || deleteConfirmation.isMultiCourt) 
              ? "Delete Grouped Booking" 
              : "Delete Booking"
        }
        message={
          deleteConfirmation.isJoinedBooking
            ? "Are you sure you want to leave this public booking?"
            : (deleteConfirmation.isRecurring || deleteConfirmation.isMultiCourt)
              ? `Delete single booking for ${getCourtDisplayLabel(deleteConfirmation.booking.court)} on ${new Date(deleteConfirmation.booking.bookingDate).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })} at ${deleteConfirmation.booking.startTime} or delete all bookings for this grouped booking?`
              : "Are you sure you want to delete this booking?"
        }
        isGrouping={deleteConfirmation.isRecurring || deleteConfirmation.isMultiCourt}
        isJoinedBooking={deleteConfirmation.isJoinedBooking}
      />

      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={closeErrorModal}
        errorMessage={error}
      />

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        totalPaymentAmount={paymentDetails?.totalPaymentAmount || 0}
        reference={paymentDetails?.reference || ''}
        onPaymentComplete={handlePaymentComplete}
        paymentDetails={paymentDetails}
        customText={paymentDetails?.customText || null}
      />
    </div>
  );
};

export default Dashboard;
